import { useMemo } from 'react';

import { getVisibleCommunicationBlocks, isManagerAssessmentBlock } from './communicationBlocks';

import type { CommunicationBlockDefinition } from './communicationBlocks';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { useManagerAssessmentAccess } from '@/hooks/performance-tier/useManagerAssessmentAccess';

export interface VisibleCommunicationBlocks {
    /** Every block the step shows. */
    visibleBlocks: CommunicationBlockDefinition[];
    /**
     * The shown blocks the step may change, validate and write. A read-only Manager Assessment's
     * blocks are left out, so the save carries them forward exactly as stored.
     */
    editableBlocks: CommunicationBlockDefinition[];
    /** Whether a shown block can only be read. */
    isBlockReadOnly: (id: string) => boolean;
}

/**
 * The communication blocks of a cycle: its checked types. Manager Assessment's blocks are left out
 * short of Performance PRO, unless the cycle already has one: then they show, read-only.
 */
export const useVisibleCommunicationBlocks = (
    group: CycleEvaluationGroup | undefined,
    checkedTypes: readonly EvaluationTypeValue[],
): VisibleCommunicationBlocks => {
    const managerAssessment = useManagerAssessmentAccess(group);

    return useMemo(() => {
        const visibleBlocks = getVisibleCommunicationBlocks(
            checkedTypes,
            managerAssessment.isShown,
        );
        const isBlockReadOnly = (id: string) =>
            managerAssessment.isReadOnly && isManagerAssessmentBlock(id);

        return {
            visibleBlocks,
            editableBlocks: visibleBlocks.filter((block) => !isBlockReadOnly(block.id)),
            isBlockReadOnly,
        };
    }, [checkedTypes, managerAssessment.isShown, managerAssessment.isReadOnly]);
};
